<template>
  <div class="fts-mobile-chart" :class="{ 'fts-mobile-chart--charts': !loading && !error && hasCharts }">
    <fts-spinner full-screen v-if="loading" />
    <fts-mobile-error :error-message="error" v-else-if="error" />
    <fts-dashboard-widget
      :modules="workspaceData.vectors"
      :states="workspaceData.states"
      :users="workspaceData.users"
      :widget-data="workspaceData.widgets"
      readonly
      v-else-if="hasCharts"
    />
    <fts-mobile-error
      error-title="No Chart Found"
      error-message="There is no chart at the moment."
      v-else
    />
  </div>
</template>

<script>
import axios from 'axios';
import webAppConfig from '../commons/webApp.config';

import FTSMobileError from './FTSMobileError';
import FTSDashboardWidget from '../dashboard/FTSDashboardWidget';
import FTSSpinner from '../elements/FTSSpinner';

let resizeObserver = null;

/* SS06298: only the App opens this page, in its Dashboard tab, and the App's bottom nav bar
   floats over the webview. Marking <body> lets the stylesheet below make room for it - on the
   page and on the full-screen chart, which is appended to <body>. */
const BOTTOM_NAV_HOST = 'fts-mobile-chart-host';

export default {
  components: {
    'fts-mobile-error': FTSMobileError,
    'fts-dashboard-widget': FTSDashboardWidget,
    'fts-spinner': FTSSpinner
  },
  data: () => ({
    ready: false,
    loading: false,
    interval: null,
    error: null,
    workspaceData: null
  }),
  computed: {
    hasCharts() {
      return !!(
        this.workspaceData &&
        this.workspaceData.widgets &&
        this.workspaceData.widgets.some((x) => x.widgetType === 'chart')
      );
    }
  },
  async mounted() {
    document.body.classList.add(BOTTOM_NAV_HOST);

    if (this.$route.query.id && this.$route.query.token) {
      resizeObserver = new ResizeObserver(_.debounce(() => {
        try {
          window.eventBus.$emit("dashboard:resize");
        } catch (err) {
          console.warn('ResizeObserver:Dashboard', err);
        }
      }));
      resizeObserver.observe(this.$el);

      try {
        this.loading = true;
        this.error = null;

        const query = this.$route.query;
        let wsId = query.id;
        if(query.runas){
          wsId = `runas:${query.id}:${query.runas}`;
        }

        const res = await axios.get(`${webAppConfig.baseURL}/api/workspace/${query.id}`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${query.token}`,
            "tz": Intl.DateTimeFormat().resolvedOptions().timeZone,
            "wsId": wsId,
          }
        });
        this.workspaceData = res.data;
        this.interval = setInterval(() => {
          window.eventBus.$emit("refreshChartWidgetLastUpdated");
        }, 60000);
      } catch(err) {
        if (
          err.response &&
          (err.response.status === 400 || err.response.status === 401) &&
          err.response.data &&
          err.response.data.message
        ) {
          this.error = err.response.data.message;
        } else {
          this.error = "Page you trying to access is unavailable."
        }
      } finally {
        this.loading = false;
      }
    } else {
      this.error = "Page you trying to access is unavailable.\n Error: missing query parameters.";
    }
  },
  beforeDestroy() {
    document.body.classList.remove(BOTTOM_NAV_HOST);

    if (resizeObserver) {
      resizeObserver.unobserve(this.$el);
      resizeObserver.disconnect();
    }
    clearInterval(this.interval);
    this.interval = null;
  }
}
</script>

<style lang="scss" scoped>
.fts-mobile-chart {
  height: 100%;
  padding: 0.5rem;
}
</style>

<style lang="scss">
/* SS06298: the App's floating bottom nav bar - 64px tall with 20px of padding above and below,
   plus whatever of the safe area its lower padding does not already cover (flutterApp
   homePage.dart bottomNavOverlayHeight). The last chart and the full-screen chart scroll
   clear of it instead of ending underneath. */
body.fts-mobile-chart-host {
  --fts-app-bottom-nav: calc(104px + max(env(safe-area-inset-bottom, 0px) - 20px, 0px));
}

body.fts-mobile-chart-host .fts-mobile-chart {
  padding-bottom: calc(0.5rem + var(--fts-app-bottom-nav));
}

/* SS06298 (reopened 10-19): the page box is height: 100% so its spinner and error pages can
   centre. With charts it must grow with them instead: the charts overflow a viewport-high box,
   and its bottom padding then sat at the viewport's foot, inside the box, not after the last
   chart - which still ended under the bar. */
body.fts-mobile-chart-host .fts-mobile-chart.fts-mobile-chart--charts {
  height: auto;
  min-height: 100%;
}

body.fts-mobile-chart-host .d-widget-chart-modal .d-widget-chart-modal__body {
  padding-bottom: var(--fts-app-bottom-nav);
}
</style>